import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { account, auth, type AccountOverview, type Calibration } from '../lib/api';
import { FEATURES } from '../lib/features';
import { useSession } from '../lib/session';
import { LOCALES, useT } from '../lib/i18n';
import { useErrorText } from '../lib/errors';
import { useLoad } from '../lib/useLoad';
import type { Theme } from '../lib/theme';
import { useToast } from '../components/Toaster';
import { Skeleton } from '../components/Skeleton';
import { usePreferences } from '../components/Preferences';
import { SignOutIcon, TrashIcon } from '../components/Icons';
import { describeDevice } from '../lib/device';
import { useDateFormat } from '../lib/dates';

type Part = 'profile' | 'appearance' | 'usage' | 'security';

const PARTS: Part[] = ['profile', 'appearance', 'usage', 'security'];

const anchor = (part: Part) => `settings-${part}`;

/**
 * Profile, appearance, usage and security, on one page with a table of contents.
 *
 * One scrolling page rather than tabs: everything about an account is short,
 * and seeing it all at once answers "where was that setting" before it is
 * asked. The contents stay beside it so any part is one click away, and they
 * follow the scroll so they always say where you are.
 */
export function SettingsPage() {
  const t = useT();
  const { user, refresh } = useSession();
  const [params] = useSearchParams();
  const asked = params.get('tab');
  const [active, setActive] = useState<Part>(PARTS.find((part) => part === asked) ?? 'profile');
  const { data: overview, reload: load } = useLoad(() => account.overview(), {
    initial: undefined as AccountOverview | undefined,
  });
  const ready = Boolean(overview);

  // A link to `?tab=security` lands on that part once there is a page to scroll.
  useEffect(() => {
    if (!ready || !asked) return;
    document.getElementById(anchor(asked as Part))?.scrollIntoView({ block: 'start' });
  }, [ready, asked]);

  // The contents follow the reading: the part nearest the top is the current one.
  useEffect(() => {
    if (!ready || typeof IntersectionObserver === 'undefined') return;
    const watcher = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (top) setActive(top.target.id.replace('settings-', '') as Part);
      },
      { rootMargin: '-15% 0px -65% 0px' },
    );
    PARTS.forEach((part) => {
      const node = document.getElementById(anchor(part));
      if (node) watcher.observe(node);
    });
    return () => watcher.disconnect();
  }, [ready]);

  /*
   * With accounts switched off — which is how the desktop shell runs — there is
   * no account to settle. Saying so beats the blank page this used to render
   * for anyone who reached the route.
   */
  if (!user) {
    return (
      <div className="app__inner">
        <header className="masthead">
          <div>
            <div className="label masthead__eyebrow">{t('settings.eyebrow')}</div>
            <h1>{t('settings.title')}</h1>
            <p>{t('settings.noAccounts')}</p>
          </div>
        </header>
      </div>
    );
  }

  const go = (part: Part) => {
    setActive(part);
    document.getElementById(anchor(part))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="app__inner settings">
      <aside className="settings__toc">
        <h1 className="settings__title">{t('settings.title')}</h1>
        <nav className="settings__nav" aria-label={t('settings.title')}>
          {PARTS.map((part, i) => (
            <a
              key={part}
              href={`#${anchor(part)}`}
              className={active === part ? 'is-on' : ''}
              aria-current={active === part ? 'true' : undefined}
              onClick={(event) => {
                event.preventDefault();
                go(part);
              }}
            >
              <b>{String(i + 1).padStart(2, '0')}</b>
              {t(`settings.tab.${part}`)}
            </a>
          ))}
        </nav>
      </aside>

      <div className="settings__body">
        {!overview ? (
          <Skeleton count={3} ratio="4:5" />
        ) : (
          <>
            <Section part="profile" n={1}>
              <Profile
                overview={overview}
                onSaved={() => {
                  load();
                  void refresh();
                }}
              />
            </Section>
            <Section part="appearance" n={2}>
              <Appearance />
            </Section>
            <Section part="usage" n={3}>
              <Usage overview={overview} />
            </Section>
            <Section part="security" n={4}>
              <Security overview={overview} />
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

/** One part of the page: a numbered heading, then its slabs. */
function Section({ part, n, children }: { part: Part; n: number; children: ReactNode }) {
  const t = useT();
  return (
    <section id={anchor(part)} className="settings__part" aria-labelledby={`${anchor(part)}-h`}>
      <h2 id={`${anchor(part)}-h`} className="settings__h">
        <small>{String(n).padStart(2, '0')}</small>
        {t(`settings.tab.${part}`)}
      </h2>
      {children}
    </section>
  );
}

/** A setting: what it is on the left, the control on the right. */
function Row({
  label,
  hint,
  htmlFor,
  stacked,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  /** A list rather than a control: under the name, the full width of the slab. */
  stacked?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={stacked ? 'setrow setrow--stacked' : 'setrow'}>
      <div className="setrow__text">
        {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : <span>{label}</span>}
        {hint && <p className="faint">{hint}</p>}
      </div>
      <div className="setrow__ctl">{children}</div>
    </div>
  );
}

/* -------------------------------- profile ------------------------------- */

function Profile({ overview, onSaved }: { overview: AccountOverview; onSaved: () => void }) {
  const t = useT();
  const describe = useErrorText();
  const toast = useToast();
  const [name, setName] = useState(overview.user.name);
  const [accountName, setAccountName] = useState(overview.account?.name ?? '');
  const [busy, setBusy] = useState(false);

  const dirty = name !== overview.user.name || accountName !== (overview.account?.name ?? '');
  const shown = overview.user.name || overview.user.email;

  const save = async () => {
    setBusy(true);
    try {
      await account.update({ name, accountName });
      toast(t('settings.saved'), 'ok');
      onSaved();
    } catch (err) {
      toast(describe(err, 'settings.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel setpanel">
      <div className="setrow setrow--who">
        <span className="setwho__avatar" aria-hidden="true">
          {shown.slice(0, 1).toUpperCase()}
        </span>
        <span className="setwho__text">
          <strong>{shown}</strong>
          {overview.account?.name && <span className="faint">{overview.account.name}</span>}
        </span>
      </div>
      <Row label={t('auth.name')} hint={t('settings.nameHint')} htmlFor="p-name">
        <input id="p-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Row>
      <Row label={t('settings.studioName')} hint={t('settings.studioHint')} htmlFor="p-account">
        <input
          id="p-account"
          className="input"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
        />
      </Row>
      <Row label={t('auth.email')} hint={t('settings.emailFixed')}>
        <span className="setrow__value">{overview.user.email}</span>
      </Row>
      {overview.members.length > 1 && (
        <Row label={t('settings.members')} stacked>
          <ul className="setlist">
            {overview.members.map((m) => (
              <li key={m.id}>
                <span>{m.name || m.email}</span>
                <span className="faint mono">{m.role}</span>
              </li>
            ))}
          </ul>
        </Row>
      )}
      <div className="setpanel__foot">
        <button className="btn btn--primary" disabled={!dirty || busy} onClick={() => void save()}>
          {t(busy ? 'caster.saving' : 'common.save')}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------ appearance ------------------------------ */

/** The three rooms, each shown as a tiny screen of itself. */
const ROOMS: Array<{ id: Theme; key: string }> = [
  { id: 'light', key: 'app.theme.light' },
  { id: 'dark', key: 'app.theme.dark' },
  { id: 'system', key: 'app.theme.system' },
];

function Appearance() {
  const t = useT();
  const { theme, locale, chooseTheme, chooseLocale } = usePreferences();
  return (
    <div className="panel setpanel">
      <Row label={t('settings.theme')} hint={t('settings.themeHint')}>
        <div className="rooms" role="group" aria-label={t('app.theme')}>
          {ROOMS.map((room) => (
            <button
              key={room.id}
              type="button"
              className={`room${theme === room.id ? ' is-on' : ''}`}
              aria-pressed={theme === room.id}
              onClick={() => chooseTheme(room.id)}
            >
              <span className={`room__screen room__screen--${room.id}`} aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              {t(room.key)}
            </button>
          ))}
        </div>
      </Row>
      <Row label={t('app.language')} hint={t('settings.languageHint')}>
        <div className="segmented" role="group" aria-label={t('app.language')}>
          {LOCALES.map((option) => (
            <button
              key={option.id}
              type="button"
              lang={option.id}
              className={locale === option.id ? 'is-on' : ''}
              aria-pressed={locale === option.id}
              onClick={() => chooseLocale(option.id)}
            >
              {option.long}
            </button>
          ))}
        </div>
      </Row>
    </div>
  );
}

/* --------------------------------- usage -------------------------------- */

/**
 * What the studio has made this month, and how well its judge agrees with you.
 *
 * A count, not an allowance: there are no plans and no caps, so there is no
 * bar filling up and nothing to run out of. It is here so you can see what
 * a month of work was — the provider bills your own key for it.
 */
function Usage({ overview }: { overview: AccountOverview }) {
  const t = useT();
  return (
    <>
      <div className="setusage">
        <span className="setusage__count mono">{overview.usage.frames}</span>
        <div className="setusage__text">
          <strong>{t('usage.frames')}</strong>
          <span className="mono">
            {t('usage.thisMonth')} · {overview.usage.period}
          </span>
          <p>{t('usage.noLimit')}</p>
        </div>
      </div>
      {FEATURES.calibration && <CalibrationPanel />}
    </>
  );
}

/**
 * Whether the automatic judge agrees with you.
 *
 * It sits under the usage because both kinds of disagreement are money: a
 * frame it rejected and you would have kept was rendered twice, and a frame
 * it passed and you crossed out was delivered wrong. Until there are enough
 * marks to mean anything, it says so rather than showing a number.
 */
function CalibrationPanel() {
  const t = useT();
  const { data } = useLoad(() => account.calibration(), {
    initial: undefined as Calibration | undefined,
  });
  if (!data) return null;

  const pct = (n?: number) => (n === undefined ? '—' : `${Math.round(n * 100)}%`);

  return (
    <section className="panel stack stack--sm">
      <span className="label">{t('calib.title')}</span>

      {data.sample < data.minimum ? (
        <p className="faint">{t('calib.notEnough', { n: data.sample, min: data.minimum })}</p>
      ) : (
        <>
          <p className="muted">{t('calib.agreement', { pct: pct(data.currentAgreement), n: data.sample })}</p>
          <dl className="specs">
            <dt>{t('calib.falseRejects')}</dt>
            <dd className="mono">{data.falseRejects}</dd>
            <dt>{t('calib.falseAccepts')}</dt>
            <dd className="mono">{data.falseAccepts}</dd>
            <dt>{t('calib.keptMean')}</dt>
            <dd className="mono">{data.keptMean ?? '—'}</dd>
            <dt>{t('calib.rejectedMean')}</dt>
            <dd className="mono">{data.rejectedMean ?? '—'}</dd>
          </dl>
          {data.suggestedThreshold !== undefined && (
            <p className="faint">
              {t('calib.suggested', {
                n: data.suggestedThreshold,
                pct: pct(data.suggestedAgreement),
              })}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/* -------------------------------- security ------------------------------ */

function Security({ overview }: { overview: AccountOverview }) {
  const t = useT();
  const date = useDateFormat();
  const [allDevices, setAllDevices] = useState(false);
  const describe = useErrorText();
  const toast = useToast();
  const { signOut } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const { data: devices, setData: setDevices } = useLoad(() => auth.sessions(), {
    initial: [] as Awaited<ReturnType<typeof auth.sessions>>,
  });
  const { data: activity } = useLoad(() => account.activity(), {
    initial: [] as Awaited<ReturnType<typeof account.activity>>,
  });
  const [confirm, setConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  const change = async () => {
    setBusy(true);
    try {
      await account.changePassword(current, next);
      setCurrent('');
      setNext('');
      toast(t('settings.passwordChanged'), 'ok');
      void auth.sessions().then(setDevices);
    } catch (err) {
      toast(describe(err, 'settings.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await account.remove(deletePassword, confirm);
      await signOut();
      window.location.href = '/';
    } catch (err) {
      toast(describe(err, 'settings.saveFailed'), 'error');
    }
  };

  return (
    <>
      <div className="panel setpanel">
        <Row label={t('settings.password')} hint={t('settings.passwordEvicts')}>
          <div className="setrow__fields">
            <label className="sr-only" htmlFor="s-current">
              {t('settings.currentPassword')}
            </label>
            <input
              id="s-current"
              className="input"
              type="password"
              autoComplete="current-password"
              placeholder={t('settings.currentPassword')}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
            <label className="sr-only" htmlFor="s-next">
              {t('auth.newPassword')}
            </label>
            <input
              id="s-next"
              className="input"
              type="password"
              autoComplete="new-password"
              minLength={10}
              placeholder={t('auth.newPassword')}
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <button
              className="btn btn--primary"
              disabled={busy || current.length < 1 || next.length < 10}
              onClick={() => void change()}
            >
              {t(busy ? 'caster.saving' : 'settings.changePassword')}
            </button>
          </div>
        </Row>

        <Row label={t('settings.devices')} stacked>
          {/*
           * Each device as a person would name it — "Safari on iPhone" —
           * and when it was last used, newest first. The address and the raw
           * user-agent header said nothing anyone could recognise.
           */}
          <ul className="devices">
            {[...devices]
              .sort(
                (a, b) => Number(b.current) - Number(a.current) || b.lastSeenAt.localeCompare(a.lastSeenAt),
              )
              .slice(0, allDevices ? undefined : 4)
              .map((d) => {
                const { browser, os } = describeDevice(d.userAgent);
                const name =
                  browser && os
                    ? t('settings.deviceOn', { browser, os })
                    : browser || os || t('settings.deviceUnknown');
                return (
                  <li key={d.id} className={`devices__item${d.current ? ' is-current' : ''}`}>
                    <span className="devices__icon" aria-hidden="true">
                      {os === 'iPhone' || os === 'Android' ? <PhoneIcon /> : <ScreenIcon />}
                    </span>
                    <span className="devices__text">
                      <strong>{name}</strong>
                      <span className="faint">
                        {t('settings.lastSeen', { when: date.stamp(d.lastSeenAt) })}
                      </span>
                    </span>
                    {d.current ? (
                      /* No revoke button on the session you are reading this in:
                     signing out, just below, is that, and labelled so. */
                      <span className="devices__here">{t('settings.thisDevice')}</span>
                    ) : (
                      <button
                        className="btn btn--sm"
                        onClick={async () => {
                          await auth.revokeSession(d.id);
                          setDevices((prev) => prev.filter((x) => x.id !== d.id));
                        }}
                      >
                        {t('settings.revoke')}
                      </button>
                    )}
                  </li>
                );
              })}
          </ul>
          {/* The four most recent, and the rest on request: a long list of old
              browsers is not what anyone opened this page to read. */}
          {devices.length > 4 && (
            <button
              type="button"
              className="btn btn--sm devices__more"
              onClick={() => setAllDevices((v) => !v)}
            >
              {allDevices ? t('settings.devicesFewer') : t('settings.devicesMore', { n: devices.length - 4 })}
            </button>
          )}
        </Row>

        <Row label={t('settings.signOut')} hint={t('settings.signOutHint')}>
          <button className="btn btn--primary" onClick={() => void signOut()}>
            <SignOutIcon /> {t('settings.signOut')}
          </button>
        </Row>
      </div>

      {activity.length > 0 && (
        <details className="panel">
          <summary className="label" style={{ cursor: 'pointer' }}>
            {t('settings.activity')}
          </summary>
          <div className="log" style={{ marginTop: 14 }}>
            {activity.map((entry, i) => (
              <div key={i} className="log__line">
                <span className="log__time">{new Date(entry.at).toLocaleString()}</span>
                <span>
                  {entry.action}
                  {entry.detail ? ` · ${entry.detail}` : ''}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      <section className="panel panel--danger stack stack--sm">
        <span className="label">{t('settings.dangerZone')}</span>
        <p className="faint">{t('settings.deleteWarning', { name: overview.account?.name ?? '' })}</p>
        <div className="field">
          <label htmlFor="d-pass">{t('settings.currentPassword')}</label>
          <input
            id="d-pass"
            className="input"
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="d-confirm">{t('settings.typeName', { name: overview.account?.name ?? '' })}</label>
          <input
            id="d-confirm"
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <button
          className="btn btn--danger"
          disabled={!deletePassword || confirm !== overview.account?.name}
          onClick={() => void remove()}
        >
          <TrashIcon /> {t('settings.deleteAccount')}
        </button>
      </section>
    </>
  );
}

/** A phone, for a session on one. */
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="7" y="3" width="10" height="18" rx="2.5" />
    <path d="M11 18h2" />
  </svg>
);

/** A screen, for a session on a computer or a tablet. */
const ScreenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="3" y="5" width="18" height="12" rx="2" />
    <path d="M9 20h6" />
  </svg>
);
